import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { MIN_FILTER_YEAR, THROTTLE_LIMIT } from '@/constants';
import { formatDate } from '@/helpers';
import { useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';

import { CloseIcon } from '@/assets/icons';
import {
  Button,
  DateRangePicker,
  IDateRange,
  Loading,
  NotFoundResult,
  Pagination,
  Search,
  SingleFilter,
  SingleFilterContent,
  SingleFilterItem,
  SingleFilterTrigger,
  SingleFilterValue,
  SortDirection,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';

import { FieldChanged } from '@/features/user-management/enums';
import { getFieldChangedLabel } from '@/features/user-management/utils';

import {
  ALL_FIELD_CHANGED_OPTION,
  FIELD_CHANGED_OPTIONS,
} from '@/features/user-management/user-management-list/constants';
import { useAuditLogSearching } from '@/features/user-management/user-management-list/hooks';
import {
  IAuditLog,
  IAuditLogSearchingPayload,
} from '@/features/user-management/user-management-list/interfaces';
import { AuditLogField } from '@/features/user-management/user-management-list/enums';

export const AuditLog = () => {
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const pageParam = Number(searchParams.get('page'));
  const initialPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize] = useState<number>(10);

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [fieldChanged, setFieldChanged] = useState<string>(
    searchParams.get('fieldChanged') || ALL_FIELD_CHANGED_OPTION.value,
  );
  const [auditTime, setAuditTime] = useState<IDateRange>({
    from: searchParams.get('from') ? new Date(searchParams.get('from') as string) : undefined,
    to: searchParams.get('to') ? new Date(searchParams.get('to') as string) : undefined,
  });

  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || 'id');
  const [isDescending, setDescending] = useState<boolean>(searchParams.get('desc') !== 'false');

  const previousFilterKeyRef = useRef<string | null>(null);
  const dateRangePickerRef = useRef<any>(null);

  const isFiltered = useMemo(
    () =>
      searchQuery.length > 0 ||
      fieldChanged !== ALL_FIELD_CHANGED_OPTION.value ||
      (auditTime.from !== undefined && auditTime.to !== undefined),
    [fieldChanged, searchQuery, auditTime],
  );

  const onClearFilters = () => {
    navigate(
      {
        pathname: routerLocation.pathname,
        hash: routerLocation.hash,
      },
      { replace: true },
    );
    setSearchQuery('');
    setFieldChanged(ALL_FIELD_CHANGED_OPTION.value);
    setAuditTime({ from: undefined, to: undefined });
    dateRangePickerRef.current?.onClear();
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery);

  // // Throttle filters and sorting
  const throttledFieldChanged = useThrottle(fieldChanged, THROTTLE_LIMIT, true);
  const throttledAuditTime = useThrottle(auditTime, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Handle column sorting
  const onSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setDescending(true);
    } else {
      setSortBy(columnKey);
      setDescending(direction === 'desc');
    }
  };

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: IAuditLogSearchingPayload = {
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    if (throttledFieldChanged && throttledFieldChanged !== ALL_FIELD_CHANGED_OPTION.value) {
      payload.fieldName = throttledFieldChanged as FieldChanged;
    }

    if (throttledAuditTime?.from && throttledAuditTime.to) {
      payload.startTime = throttledAuditTime.from;
      payload.endTime = throttledAuditTime.to;
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    return payload;
  }, [
    debouncedSearchQuery,
    currentPage,
    throttledFieldChanged,
    throttledAuditTime,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { data, isLoading, isFetching } = useAuditLogSearching(apiPayload);

  const auditLogs = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata?.totalPages || 0, [data?.metadata?.totalPages]);
  const totalItems = useMemo(() => data?.metadata?.totalItems || 0, [data?.metadata?.totalItems]);
  const isEmpty = useMemo(() => auditLogs.length === 0, [auditLogs]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', String(currentPage));
    if (searchQuery) params.set('search', searchQuery);
    if (fieldChanged) params.set('fieldChanged', fieldChanged);
    if (auditTime.from && auditTime.to) {
      params.set('from', auditTime.from?.toISOString() || '');
      params.set('to', auditTime.to?.toISOString() || '');
    }
    if (sortBy !== 'id') params.set('sortBy', sortBy);
    if (!isDescending) params.set('desc', 'false');

    // Navigate with an explicit location object so react-router keeps the current hash
    navigate(
      {
        pathname: routerLocation.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
        hash: routerLocation.hash,
      },
      { replace: true },
    );
  }, [
    currentPage,
    searchQuery,
    fieldChanged,
    auditTime,
    sortBy,
    isDescending,
    routerLocation.hash,
    routerLocation.pathname,
  ]);

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledFieldChanged,
      throttledAuditTime,
      throttledSortBy,
      throttledIsDescending,
    ]);

    if (previousFilterKeyRef.current === null) {
      // First run — store baseline, don't reset
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [
    debouncedSearchQuery,
    throttledFieldChanged,
    throttledAuditTime,
    throttledSortBy,
    throttledIsDescending,
  ]);

  return (
    <div>
      <div className="flex justify-between w-full">
        <div className="flex flex-row flex-wrap justify-start items-center gap-2">
          <Search
            placeholder="Search by target user email, changed by"
            className="w-[400px] sm:w-[400px] gap-2"
            style={{ paddingRight: '16px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <DateRangePicker
            ref={dateRangePickerRef}
            required
            allowClear
            initialDateFrom={auditTime.from}
            initialDateTo={auditTime.to}
            onUpdate={({ range }) => setAuditTime(range)}
            align="start"
            locale="en-GB"
            buttonClassName="!w-[300px] sm:!w-[300px]"
            placeholder="Audit Time"
            minYear={MIN_FILTER_YEAR}
          />
          <SingleFilter
            value={fieldChanged}
            defaultValue={ALL_FIELD_CHANGED_OPTION.value}
            onValueChange={(value) => setFieldChanged(value)}
          >
            <SingleFilterTrigger className="!w-[280px]">
              <SingleFilterValue placeholder="All Field Changed" />
            </SingleFilterTrigger>
            <SingleFilterContent>
              {FIELD_CHANGED_OPTIONS.map((option) => (
                <SingleFilterItem key={option.value} value={option.value}>
                  {option.label}
                </SingleFilterItem>
              ))}
            </SingleFilterContent>
          </SingleFilter>
          {isFiltered && (
            <Button
              buttonColor="primary"
              buttonStyle="textlink"
              prefixIcon={<CloseIcon className="size-5" />}
              onClick={onClearFilters}
              size="default"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      <div className="w-full relative">
        {isEmpty && !isLoading && !isFetching ? (
          <NotFoundResult label="No audit logs." />
        ) : (
          <>
            <div className="w-full pb-3 mb-3 overflow-auto custom-scrollbar">
              <Table className="mt-4 min-w-full !w-fit !overflow-hidden" size="sm" template="zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead
                      sortable
                      sortDirection={sortBy === 'id' ? (isDescending ? 'desc' : 'asc') : null}
                      onSort={(direction) => onSort('id', direction)}
                    >
                      S/N
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === AuditLogField.CreatedAt ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(AuditLogField.CreatedAt, direction)}
                    >
                      Date & Time
                    </TableHead>
                    <TableHead>Target User Email</TableHead>
                    <TableHead>Changed By</TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === 'field_name' ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort('field_name', direction)}
                    >
                      Field Changed
                    </TableHead>
                    <TableHead>Old Value</TableHead>
                    <TableHead>New Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((auditLog: IAuditLog) => (
                    <TableRow key={auditLog.id} rowId={auditLog.id}>
                      <TableCell className="w-[74px] text-neutral-9">{auditLog.id}</TableCell>
                      <TableCell className="w-[176px] text-neutral-9">
                        {formatDate({
                          date: new Date(auditLog.createdAt),
                          hasTime: true,
                        })}
                      </TableCell>
                      <TableCell className="w-[280px]">
                        <span
                          className="block truncate text-neutral-9"
                          title={auditLog.targetUserEmail}
                        >
                          {auditLog.targetUserEmail}
                        </span>
                      </TableCell>
                      <TableCell className="w-[186px]">
                        <span
                          className="block truncate text-neutral-9"
                          title={auditLog.createdName}
                        >
                          {auditLog.createdName}
                        </span>
                      </TableCell>
                      <TableCell className="w-[160px] text-neutral-9">
                        {getFieldChangedLabel(auditLog.fieldName)}
                      </TableCell>
                      <TableCell className="w-[258px] text-neutral-9">
                        {auditLog.oldValue ? (
                          <span className="block truncate" title={auditLog.oldValue}>
                            {auditLog.oldValue}
                          </span>
                        ) : (
                          <span className="tracking-[1px]">--</span>
                        )}
                      </TableCell>
                      <TableCell className="w-[258px] text-neutral-9">
                        {auditLog.newValue ? (
                          <span className="block truncate" title={auditLog.newValue}>
                            {auditLog.newValue}
                          </span>
                        ) : (
                          <span className="tracking-[1px]">--</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={(page) => setCurrentPage(page)}
              />
            )}
          </>
        )}
        {(isLoading || isFetching) && <Loading fullScreen />}
      </div>
    </div>
  );
};
